/**
 * AuthPage — operator-UI sign-in surface (PAGES.md 07, canvas 7b; ANIMATIONS.md
 * `components/ui/auth-page.tsx`, ported to this JSX/CRA tree).
 *
 * PRESENTATION ONLY. It owns no auth logic: LoginPage passes the field values,
 * the change handlers, the submit handler, the busy flag and the error text,
 * so the API call (`POST /api/auth/login`), the redirects and the SSO arrival
 * path stay exactly where they were.
 *
 * Kept from the approved component: accent lines at 18/50/82% (horizontal) and
 * 22/50/78% (vertical), rising particles, the card fading up after 0.4s,
 * "IV BRANDS · HUB", "INTERNAL · TEAM ONLY", email + password only.
 * Not rendered (the CRM has no route or backend behaviour for them, see
 * FLAGGED.md phase C): Request access, Remember me, Forgot password, sign-up.
 *
 * Motion: the lines / card animations are CSS (index.css `.mb-auth-*`) and are
 * switched off under prefers-reduced-motion; the particle canvas then paints a
 * single static frame and never starts its rAF loop. The loop also pauses while
 * the tab is hidden.
 */
import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { prefersReducedMotion } from "../../lib/motion";

function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c && c.getContext ? c.getContext("2d") : null;
    if (!c || !ctx) return undefined;
    const reduced = prefersReducedMotion();
    let raf = 0;
    let ps = [];
    const make = () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      v: Math.random() * 0.25 + 0.05,
      o: Math.random() * 0.35 + 0.15,
    });
    const paint = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.fillStyle = "rgb(250,250,250)";
      for (const p of ps) {
        ctx.globalAlpha = p.o; // same rgba(250,250,250,o) without a string per particle
        ctx.fillRect(p.x, p.y, 0.7, 2.2);
      }
      ctx.globalAlpha = 1;
    };
    const init = () => {
      c.width = window.innerWidth;
      c.height = window.innerHeight;
      ps = Array.from({ length: Math.floor((c.width * c.height) / 9000) }, make);
      if (reduced) paint();
    };
    let last = 0;
    const draw = (now) => {
      raf = 0;
      if (document.hidden) return; // resumed by visibilitychange
      // ~60 fps cap: particle speeds are per 60 Hz frame (approved component).
      if (now - last < 1000 / 60 - 1) { raf = requestAnimationFrame(draw); return; }
      last = now;
      for (const p of ps) {
        p.y -= p.v;
        if (p.y < 0) Object.assign(p, make(), { y: c.height + Math.random() * 40 });
      }
      paint();
      raf = requestAnimationFrame(draw);
    };
    const onVis = () => {
      if (!document.hidden && !raf && !reduced) raf = requestAnimationFrame(draw);
    };
    init();
    if (!reduced) raf = requestAnimationFrame(draw);
    window.addEventListener("resize", init);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", init);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      data-testid="auth-particles"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-50 mix-blend-screen"
    />
  );
}

export default function AuthPage({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  busy = false,
  error = "",
}) {
  const [show, setShow] = useState(false);

  return (
    <section className="mb-auth fixed inset-0 overflow-hidden bg-mb-bg text-mb-text" data-testid="auth-page">
      <div className="mb-auth-glow pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="mb-auth-lines" aria-hidden="true">
        <div className="mb-auth-h" />
        <div className="mb-auth-h" />
        <div className="mb-auth-h" />
        <div className="mb-auth-v" />
        <div className="mb-auth-v" />
        <div className="mb-auth-v" />
      </div>
      <Particles />

      <header className="absolute inset-x-0 top-0 flex h-16 items-center justify-between border-b border-mb-line-divider px-7">
        <span className="font-mono text-caption tracking-caps-wide text-mb-text-muted">IV BRANDS · HUB</span>
      </header>

      <div className="relative grid h-full place-items-center overflow-y-auto px-4">
        <div className="mb-auth-card w-full max-w-[400px] p-7 backdrop-blur" data-testid="auth-card">
          <div className="mb-5 space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
            <p className="text-sm text-mb-text-muted">Sign in to the Iv Brands hub</p>
          </div>

          <form onSubmit={onSubmit} className="grid gap-4" data-testid="login-form">
            <div className="grid gap-1.5">
              <label htmlFor="login-email" className="text-body font-medium text-mb-text-secondary">
                Email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mb-text-faint" aria-hidden="true" />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={onEmailChange}
                  placeholder="you@ivbrands.co"
                  className="mb-input mb-input-lg mb-auth-input pl-10"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <label htmlFor="login-password" className="text-body font-medium text-mb-text-secondary">
                Password
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mb-text-faint" aria-hidden="true" />
                <input
                  id="login-password"
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={onPasswordChange}
                  placeholder="••••••••"
                  className="mb-input mb-input-lg mb-auth-input pl-10 pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  aria-label={show ? "Hide password" : "Show password"}
                  aria-pressed={show}
                  onClick={() => setShow((v) => !v)}
                  className="mb-btn mb-btn-ghost mb-btn-icon absolute right-1 top-1/2 -translate-y-1/2"
                  data-testid="login-password-toggle"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error ? (
              <p className="text-caption text-mb-danger-text" role="alert" data-testid="login-error">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="mb-btn mb-btn-primary mb-btn-lg mb-auth-submit w-full"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Signing in…" : "Continue"}
            </button>
          </form>
        </div>
      </div>

      <span className="absolute bottom-5 left-7 mb-auth-footnote font-mono text-label" aria-hidden="true">
        INTERNAL · TEAM ONLY
      </span>
    </section>
  );
}
